import React from "react";
import PropTypes from "prop-types";
import { UserX } from "lucide-react";

import { UIEmptyState, UIPaginationBar } from "../../../ui";
import ApplicantsBulkActions from "./ApplicantsBulkActions";
import ApplicantsFilters from "./ApplicantsFilters";
import ApplicantsTable from "./ApplicantsTable";
import {
  hasActiveApplicantsFilters,
  hasActiveApplicantsSearch,
} from "./eventRequestMappers";
import { joiningRequestStatuses } from "./constants";

const ApplicantsListPanel = ({
  activeStatus,
  applicantsTab,
  bulkActionsCopy,
  canAdd = true,
  canEdit = true,
  canDelete = true,
  canView = true,
  filters,
  joiningRequestsCopy,
  localizedRows,
  nationalityLoading,
  nationalityOptions,
  nationalitySearch,
  page,
  pageSize,
  pages,
  rangeFrom,
  rangeTo,
  ratingOptions,
  cityOptions,
  renderJoinedExportButton,
  roleOptions,
  rows,
  search,
  selectedIds,
  selectedLabel,
  shiftFilterOptions,
  tableLabels,
  total,
  zoneOptions,
  onAddEditTag,
  onAllocate,
  onApprove,
  onAccept,
  onDeleteTag,
  isEditTagDisabled = false,
  isDeleteTagDisabled = false,
  canManageRowTag,
  onEditRole,
  onExport,
  onFilterChange,
  onNationalityOpen,
  onNationalitySearchChange,
  onTagClick,
  onPageChange,
  onPageSizeChange,
  onReject,
  onRemove,
  onSearchChange,
  onSelectAll,
  onSelectRow,
  onRowClick,
  onUnassign,
  onReactivate,
  onEditReason,
  showApplicantTypeFilter = true,
  showCheckInFilter = false,
  applicantsActionsLocked = false,
  statusTabTotal = 0,
}) => {
  const showSelection = canAdd || canEdit || canDelete;
  // Export is view-gated (Phase 6 decision), so a view-only role still needs
  // the toolbar to render even with no add/edit/delete access.
  const showBulkActionsBar = canAdd || canEdit || canDelete || canView;
  const isApplicantTypeFilterActive = Boolean(filters.applicantType?.value);
  const filtersExcludingApplicantType = {
    ...filters,
    applicantType: null,
  };
  const hasSearchOrFilter =
    hasActiveApplicantsSearch(search) ||
    hasActiveApplicantsFilters(filtersExcludingApplicantType);
  const showFilteredEmptyState =
    hasSearchOrFilter && statusTabTotal > 0 && !isApplicantTypeFilterActive;
  const emptyStateCopy =
    joiningRequestsCopy.emptyStates[activeStatus] ??
    (activeStatus === joiningRequestStatuses.joined
      ? joiningRequestsCopy.emptyStates.joined
      : activeStatus === joiningRequestStatuses.rejected
        ? joiningRequestsCopy.emptyStates.rejected
        : activeStatus === joiningRequestStatuses.removed
          ? joiningRequestsCopy.emptyStates.removed
        : joiningRequestsCopy.emptyStates.pending);

  return (
    <div className="event-applicants__panel">
    <ApplicantsFilters
      activeStatus={activeStatus}
      applicantsTab={applicantsTab}
      tableLabels={tableLabels}
      copy={joiningRequestsCopy}
      nationalityOptions={nationalityOptions}
      roleOptions={roleOptions}
      shiftOptions={shiftFilterOptions}
      zoneOptions={zoneOptions}
      ratingOptions={ratingOptions}
      cityOptions={cityOptions}
      search={search}
      nationalitySearch={nationalitySearch}
      nationalityLoading={nationalityLoading}
      filters={filters}
      onSearchChange={onSearchChange}
      onNationalitySearchChange={onNationalitySearchChange}
      onNationalityOpen={onNationalityOpen}
      onFilterChange={onFilterChange}
      renderActionBeforeFilterToggle={renderJoinedExportButton}
      showApplicantTypeFilter={showApplicantTypeFilter}
      showCheckInFilter={showCheckInFilter}
    />
    {rows.length ? (
      <>
        {showBulkActionsBar && (
          <ApplicantsBulkActions
            activeStatus={activeStatus}
            labels={bulkActionsCopy}
            selectedCount={selectedIds.length}
            selectedLabel={selectedLabel}
            canAdd={canAdd}
            canEdit={canEdit}
            canDelete={canDelete}
            canView={canView}
            onApprove={onApprove}
            onAccept={onAccept || onApprove}
            onReject={onReject}
            onUnassign={onUnassign}
            onAllocate={onAllocate}
            onExport={onExport}
            onEditRole={onEditRole}
            onRemove={onRemove}
            onReactivate={onReactivate}
            onEditReason={onEditReason}
            onAddEditTag={onAddEditTag}
            onDeleteTag={onDeleteTag}
            isEditTagDisabled={isEditTagDisabled}
            isDeleteTagDisabled={isDeleteTagDisabled}
            applicantsActionsLocked={applicantsActionsLocked}
          />
        )}
        <ApplicantsTable
          rows={localizedRows}
          activeStatus={activeStatus}
          selectedIds={selectedIds}
          labels={tableLabels}
          showSelection={showSelection}
          canEditTags={canEdit}
          canManageRowTag={canManageRowTag}
          onSelectRow={onSelectRow}
          onSelectAll={onSelectAll}
          onTagClick={onTagClick}
          onRowClick={onRowClick}
        />
        <UIPaginationBar
          currentPage={page}
          pages={pages}
          pageSize={pageSize}
          rangeTemplate={joiningRequestsCopy.rangeTemplate}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          rangeTotal={total}
          rowsPerPageLabel={joiningRequestsCopy.rowsPerPageLabel}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </>
      ) : isApplicantTypeFilterActive ? (
        <UIEmptyState
          classNames={{
            root: "event-applicants__empty",
            iconCircle: "event-applicants__empty-icon-circle",
          }}
          title={joiningRequestsCopy.applicantTypeFilter?.emptyState?.[filters.applicantType.value]?.title}
          description={joiningRequestsCopy.applicantTypeFilter?.emptyState?.[filters.applicantType.value]?.description}
          icon={<UserX size={40} />}
        />
      ) : showFilteredEmptyState ? (
        <UIEmptyState
          classNames={{
            root: "event-applicants__empty",
            iconCircle: "event-applicants__empty-icon-circle",
          }}
          title={joiningRequestsCopy.filteredEmptyState.title}
          description={joiningRequestsCopy.filteredEmptyState.description}
          icon={<UserX size={40} />}
        />
      ) : (
        <UIEmptyState
          classNames={{
            root: "event-applicants__empty",
            iconCircle: "event-applicants__empty-icon-circle",
          }}
          title={emptyStateCopy.title}
          description={emptyStateCopy.description}
          icon={<UserX size={40} />}
        />
      )}
    </div>
  );
};

ApplicantsListPanel.propTypes = {
  activeStatus: PropTypes.string.isRequired,
  applicantsTab: PropTypes.object.isRequired,
  bulkActionsCopy: PropTypes.object.isRequired,
  canAdd: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  canView: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  joiningRequestsCopy: PropTypes.object.isRequired,
  localizedRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  nationalityLoading: PropTypes.bool.isRequired,
  nationalityOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  nationalitySearch: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  rangeFrom: PropTypes.number.isRequired,
  rangeTo: PropTypes.number.isRequired,
  ratingOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  cityOptions: PropTypes.arrayOf(PropTypes.object),
  renderJoinedExportButton: PropTypes.node,
  roleOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  search: PropTypes.string.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedLabel: PropTypes.string.isRequired,
  shiftFilterOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  tableLabels: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
  zoneOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAddEditTag: PropTypes.func.isRequired,
  onAllocate: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onAccept: PropTypes.func,
  onDeleteTag: PropTypes.func.isRequired,
  isEditTagDisabled: PropTypes.bool,
  isDeleteTagDisabled: PropTypes.bool,
  canManageRowTag: PropTypes.func,
  onEditRole: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onNationalityOpen: PropTypes.func.isRequired,
  onNationalitySearchChange: PropTypes.func.isRequired,
  onTagClick: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onSelectRow: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  onUnassign: PropTypes.func.isRequired,
  onReactivate: PropTypes.func,
  onEditReason: PropTypes.func,
  showApplicantTypeFilter: PropTypes.bool,
  showCheckInFilter: PropTypes.bool,
  applicantsActionsLocked: PropTypes.bool,
  statusTabTotal: PropTypes.number,
};

ApplicantsListPanel.defaultProps = {
  cityOptions: [],
  renderJoinedExportButton: null,
  onAccept: undefined,
  onReactivate: undefined,
  onEditReason: undefined,
  applicantsActionsLocked: false,
  statusTabTotal: 0,
  isEditTagDisabled: false,
  isDeleteTagDisabled: false,
  canManageRowTag: undefined,
};

export default ApplicantsListPanel;
